import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function LiveChat() {
  const [open, setOpen] = useState(false);
  const [visitor, setVisitor] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("serpens-chat-visitor")) || null;
    } catch {
      return null;
    }
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi there. I am Sera from Serpens Technology. Share your question and our team will reply directly to your email — usually within a few hours." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const startChat = (e) => {
    e.preventDefault();
    const v = { name: name.trim(), email: email.trim() };
    localStorage.setItem("serpens-chat-visitor", JSON.stringify(v));
    setVisitor(v);
    setMessages((m) => [...m, { from: "bot", text: `Great to meet you, ${v.name}. How can we help you today?` }]);
  };

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { from: "user", text }]);
    setInput("");
    setSending(true);
    try {
      await api.chatSend({ name: visitor.name, email: visitor.email, message: text });
      setTimeout(() => {
        setMessages((m) => [...m, { from: "bot", text: "Got it — your message is with our team now. We will reply to your email shortly. For anything urgent, WhatsApp us anytime." }]);
      }, 700);
    } catch {
      toast.error("Message could not be sent. Please try WhatsApp or the contact form.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="w-[320px] overflow-hidden rounded-2xl border border-secondary/25 bg-card shadow-2xl shadow-black/20"
            data-testid="live-chat-panel"
          >
            <div className="bg-midnight px-5 py-4">
              <p className="font-display text-sm font-semibold text-slate-50">Sera — Serpens Assistant</p>
              <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-glow" /> Messages go straight to our team
              </p>
            </div>
            <div className="h-64 space-y-3 overflow-y-auto p-4" data-testid="live-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <p className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
                    m.from === "user" ? "bg-secondary text-white rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"
                  }`}>
                    {m.text}
                  </p>
                </div>
              ))}
            </div>
            {visitor ? (
              <form onSubmit={send} className="flex items-center gap-2 border-t border-border/50 p-3">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  data-testid="live-chat-input"
                  className="h-9 flex-1 rounded-full border border-border bg-background px-3.5 text-xs outline-none focus:ring-2 focus:ring-accent/50"
                />
                <button type="submit" disabled={sending} data-testid="live-chat-send" aria-label="Send message" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-white active:scale-95 transition-transform disabled:opacity-60">
                  <Send className="h-3.5 w-3.5" />
                </button>
              </form>
            ) : (
              <form onSubmit={startChat} className="space-y-2 border-t border-border/50 p-3" data-testid="live-chat-visitor-form">
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  data-testid="live-chat-name-input"
                  className="h-9 w-full rounded-full border border-border bg-background px-3.5 text-xs outline-none focus:ring-2 focus:ring-accent/50"
                />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  data-testid="live-chat-email-input"
                  className="h-9 w-full rounded-full border border-border bg-background px-3.5 text-xs outline-none focus:ring-2 focus:ring-accent/50"
                />
                <button type="submit" data-testid="live-chat-start-button" className="h-9 w-full rounded-full bg-secondary text-xs font-semibold text-white active:scale-95 transition-transform">
                  Start Chat
                </button>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        data-testid="live-chat-button"
        aria-label="Open live chat"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-white shadow-xl shadow-blue-800/30 transition-all duration-300 hover:scale-110 hover:shadow-blue-800/50 active:scale-95"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>
    </div>
  );
}
